import { useState, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import { ebooks } from '@/mocks/ebooks';
import { useSEO } from '@/hooks/useSEO';
import EbookCover from '@/pages/ebooks/components/EbookCover';

interface FormState {
  loading: boolean;
  success: boolean;
  error: string;
}

export default function EbookLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const ebook = ebooks.find(e => e.slug === slug);

  if (!ebook) return <Navigate to="/ebooks" replace />;

  return <EbookLanding ebook={typeof ebook !== 'undefined' ? ebook : ebooks[0]} />;
}

function EbookLanding({ ebook }: { ebook: (typeof ebooks)[0] }) {
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '', empresa: '' });
  const [state, setState] = useState<FormState>({ loading: false, success: false, error: '' });
  const formRef = useRef<HTMLFormElement>(null);

  useSEO({
    title: `${ebook.title} – Ebook Gratuito | Home Office Premium`,
    description: ebook.subtitle,
    keywords: `ebook gratuito ${ebook.category}, ${ebook.title}, mobiliário corporativo Caruaru`,
    path: `/ebooks/${ebook.slug}`,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setState(prev => ({ ...prev, error: 'Preencha nome e e-mail para continuar.' }));
      return;
    }
    setState({ loading: true, success: false, error: '' });
    try {
      const body = new URLSearchParams();
      body.append('name', form.name);
      body.append('email', form.email);
      body.append('whatsapp', form.whatsapp);
      body.append('empresa', form.empresa);
      body.append('ebook', ebook.title);
      const res = await fetch(ebook.formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setState({ loading: false, success: true, error: '' });
      } else {
        setState({ loading: false, success: false, error: 'Algo deu errado. Tente novamente.' });
      }
    } catch {
      setState({ loading: false, success: false, error: 'Erro de conexão. Verifique sua internet.' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <WhatsAppFloat />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-0 relative overflow-hidden bg-foreground-950">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">

            {/* Left: info */}
            <div className="pb-16 pt-4">
              <Link
                to="/ebooks"
                className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs mb-8 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Ver todos os ebooks
              </Link>

              <span className="inline-block bg-primary-600/20 text-primary-500 text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-[0.15em] mb-5">
                {ebook.category}
              </span>

              <h1 className="text-3xl md:text-4xl lg:text-[44px] font-semibold text-white leading-[1.1] tracking-tight mb-5">
                {ebook.title}
              </h1>

              <p className="text-white/60 text-base leading-relaxed mb-8 max-w-lg font-light">
                {ebook.subtitle}
              </p>

              {/* Meta */}
              <div className="flex flex-wrap gap-5 mb-8">
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <i className="ri-pages-line text-primary-500"></i>
                  <span>{ebook.pages} páginas</span>
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <i className="ri-download-2-line text-primary-500"></i>
                  <span>Download imediato</span>
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <i className="ri-lock-line text-primary-500"></i>
                  <span>100% gratuito</span>
                </div>
              </div>

              {/* Topics */}
              <div className="space-y-2.5">
                <p className="text-white/40 text-[11px] font-semibold uppercase tracking-[0.2em] mb-3">O que você vai aprender</p>
                {ebook.topics.map((topic, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span className="text-white/70 text-sm font-light">{topic}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: cover floating */}
            <div className="flex justify-center lg:justify-end items-end pb-0">
              <div className="relative">
                <div className="absolute inset-0 blur-3xl opacity-20 bg-primary-600 rounded-full scale-75 translate-y-8"></div>
                <div className="relative w-52 h-72 md:w-64 md:h-88 rounded-2xl overflow-hidden">
                  <EbookCover ebookId={ebook.id} badge={ebook.badge} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form + description */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Description */}
            <div>
              <h2 className="text-[24px] md:text-[28px] font-semibold text-foreground-900 leading-[1.1] tracking-tight mb-4">
                Sobre este ebook
              </h2>
              <p className="text-foreground-500 leading-relaxed font-light mb-10">{ebook.description}</p>

              {/* Topics full list */}
              <h3 className="text-base font-semibold text-foreground-900 mb-5">Conteúdo completo</h3>
              <ul className="space-y-3">
                {ebook.topics.map((topic, i) => (
                  <li key={i} className="flex items-start gap-4 bg-white rounded-2xl p-5">
                    <span className="w-7 h-7 flex items-center justify-center bg-primary-50 rounded-full flex-shrink-0 mt-0.5">
                      <span className="text-primary-600 text-xs font-semibold">{i + 1}</span>
                    </span>
                    <span className="text-foreground-700 text-sm leading-relaxed">{topic}</span>
                  </li>
                ))}
              </ul>

              {/* Social proof */}
              <div className="mt-10 bg-white rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-foreground-400 text-xs">+1.200 downloads</span>
                </div>
                <p className="text-foreground-500 text-sm italic leading-relaxed font-light">
                  &quot;Conteúdo direto ao ponto, sem enrolação. Apliquei o checklist no dia seguinte e já identifiquei 3 pontos de melhoria no nosso escritório.&quot;
                </p>
                <p className="text-foreground-800 font-medium text-sm mt-3">— Empresário, Caruaru PE</p>
              </div>
            </div>

            {/* Form card */}
            <div className="lg:sticky lg:top-24">
              <div className="bg-white rounded-2xl border border-background-200/70 overflow-hidden">
                {/* Form header */}
                <div className="bg-foreground-950 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-20 flex-shrink-0 rounded-xl overflow-hidden">
                      <EbookCover ebookId={ebook.id} badge={ebook.badge} />
                    </div>
                    <div>
                      <span className="inline-block text-primary-500 text-[11px] font-semibold uppercase tracking-[0.15em] mb-1">
                        {ebook.category}
                      </span>
                      <h3 className="text-white font-semibold text-sm leading-snug">
                        {ebook.title}
                      </h3>
                      <p className="text-white/50 text-xs mt-1">{ebook.pages} páginas · Gratuito</p>
                    </div>
                  </div>
                </div>

                {/* Form body */}
                <div className="p-6">
                  {state.success ? (
                    <div className="text-center py-4">
                      <div className="w-16 h-16 flex items-center justify-center bg-primary-50 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-primary-600 text-3xl"></i>
                      </div>
                      <h4 className="text-xl font-semibold text-foreground-900 mb-2">
                        Tudo certo! Clique para baixar
                      </h4>
                      <p className="text-foreground-500 text-sm mb-6 font-light">
                        Seu ebook está pronto. Clique no botão abaixo, depois use{' '}
                        <strong className="text-foreground-800">Ctrl+P &rarr; Salvar como PDF</strong> para guardar.
                      </p>
                      <a
                        href={`${window.location.origin}${ebook.downloadUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 text-white font-medium px-6 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer text-sm mb-3"
                      >
                        <i className="ri-download-2-line text-lg"></i>
                        Baixar {ebook.title}
                      </a>
                      <p className="text-foreground-400 text-xs mb-5">Arquivo PDF · Abre em qualquer dispositivo</p>
                      <div className="border-t border-background-200/70 pt-4">
                        <p className="text-foreground-500 text-xs mb-3">Ficou com dúvida ou quer uma consultoria?</p>
                        <a
                          href="https://wa.me/5581982030285?text=Ol%C3%A1!%20Acabei%20de%20baixar%20o%20ebook%20e%20gostaria%20de%20saber%20mais"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-foreground-900 text-white font-medium px-5 py-2.5 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-whatsapp-line"></i>
                          Chamar no WhatsApp
                        </a>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h4 className="text-foreground-900 font-semibold text-base mb-1">
                        Preencha para receber gratuitamente
                      </h4>
                      <p className="text-foreground-400 text-xs mb-5">
                        Sem spam. Só conteúdo relevante para o seu negócio.
                      </p>
                      <form ref={formRef} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                            Nome completo *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Seu nome"
                            className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                            E-mail *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="seu@email.com"
                            className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                            WhatsApp
                          </label>
                          <input
                            type="text"
                            name="whatsapp"
                            value={form.whatsapp}
                            onChange={handleChange}
                            placeholder="(81) 9 0000-0000"
                            className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                            Empresa
                          </label>
                          <input
                            type="text"
                            name="empresa"
                            value={form.empresa}
                            onChange={handleChange}
                            placeholder="Nome da sua empresa (opcional)"
                            className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                          />
                        </div>
                        {state.error && (
                          <p className="text-red-500 text-xs">{state.error}</p>
                        )}
                        <button
                          type="submit"
                          disabled={state.loading}
                          className="w-full bg-primary-600 text-white font-medium py-3.5 rounded-full hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 text-sm whitespace-nowrap"
                        >
                          {state.loading ? (
                            <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                          ) : (
                            <><i className="ri-download-2-line"></i> Baixar Ebook Grátis</>
                          )}
                        </button>
                        <p className="text-center text-xs text-foreground-400">
                          <i className="ri-lock-line mr-1"></i>
                          Seus dados estão protegidos. Nunca enviamos spam.
                        </p>
                      </form>
                    </>
                  )}
                </div>
              </div>

              {/* Share box */}
              <div className="mt-4 bg-white rounded-2xl border border-background-200/70 p-5">
                <p className="text-foreground-500 text-xs font-semibold mb-3 uppercase tracking-wider">
                  Compartilhar este ebook
                </p>
                <ShareBox slug={ebook.slug} title={ebook.title} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other ebooks */}
      <OtherEbooks currentId={ebook.id} />

      {/* CTA */}
      <section className="bg-foreground-950">
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
          <h2 className="text-[28px] md:text-[36px] font-semibold text-white leading-[1.1] tracking-tight">
            Prefere uma consultoria personalizada?
          </h2>
          <p className="mt-4 text-white/50 font-light max-w-xl mx-auto">
            Nossos especialistas visitam seu espaço, avaliam cada detalhe e apresentam um projeto sob medida — sem compromisso e sem custo.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white font-medium px-8 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Agendar Visita Técnica Gratuita
            </Link>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1!%20Baixei%20um%20dos%20ebooks%20e%20gostaria%20de%20uma%20consultoria"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white font-medium px-8 py-3.5 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              Chamar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ShareBox({ slug, title }: { slug: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const url = `https://homeofficepremium.com/ebooks/${slug}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const waText = encodeURIComponent(`Olá! Encontrei este ebook gratuito sobre ${title} e achei que poderia te interessar: ${url}`);

  return (
    <div className="space-y-2">
      {/* URL display */}
      <div className="flex items-center gap-2 bg-background-50 rounded-xl px-3 py-2.5 border border-background-200/70">
        <i className="ri-link text-foreground-400 text-xs flex-shrink-0"></i>
        <span className="text-foreground-500 text-xs font-mono truncate flex-1">{url}</span>
      </div>
      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleCopy}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            copied
              ? 'bg-foreground-900 text-white'
              : 'bg-primary-600 hover:bg-primary-700 text-white'
          }`}
        >
          <i className={`${copied ? 'ri-check-line' : 'ri-clipboard-line'} text-sm`}></i>
          {copied ? 'Link copiado!' : 'Copiar link'}
        </button>
        <a
          href={`https://wa.me/?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-foreground-900 hover:bg-foreground-800 text-white rounded-xl text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-whatsapp-line text-sm"></i>
          Enviar
        </a>
      </div>
    </div>
  );
}

function OtherEbooks({ currentId }: { currentId: string }) {
  const others = ebooks.filter(e => e.id !== currentId).slice(0, 3);
  if (others.length === 0) return null;

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <h2 className="text-[22px] md:text-[28px] font-semibold text-foreground-900 leading-[1.1] tracking-tight mb-8">
          Outros ebooks que podem te interessar
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {others.map(e => (
            <Link
              key={e.id}
              to={`/ebooks/${e.slug}`}
              className="group flex gap-4 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-14 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                <EbookCover ebookId={e.id} badge={e.badge} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.15em]">{e.category}</span>
                <h3 className="text-foreground-900 font-semibold text-sm leading-snug mt-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {e.title}
                </h3>
                <p className="text-foreground-400 text-xs mt-1">{e.pages} páginas · Grátis</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}